import { CID } from "ipfs-http-client";
import { useEffect, useState } from "react";
import { getFile } from "../api/ipfs";
import { RaffleMetadata, formatEntriesList } from "../utils/formatters";
import { RaffleMetadataSchema } from "./formatters";
import { GetTokenHoldersResponseV1 } from "@/lib/friendtech";

export interface Fields {
  name: string;
  winners: number;
  entries: string;
  raffleType: "address" | "string" | "friendtech";
  minBlocksToWait: number;
}

export interface MetadataInput extends Fields {
  entriesType: "address" | "string";
}

export interface OptionalMetadata {
  friendTechMappings?: GetTokenHoldersResponseV1["users"];
}

export function createMetadata(
  { name, winners, entries, entriesType, minBlocksToWait }: MetadataInput,
  opts?: OptionalMetadata
) {
  if (!name || !winners || !entries?.length) throw new Error("Schema mismatch");

  const { entries: formattedEntries } = formatEntriesList(
    entries,
    entriesType
  )!;

  const raffleMetadata: RaffleMetadata = {
    name,
    winners,
    entries: formattedEntries,
    entriesType,
    minBlocksToWait,
  };
  if (opts?.friendTechMappings) {
    raffleMetadata.friendTechMappings = {
      version: "1",
      users: opts.friendTechMappings,
    };
  }

  return JSON.stringify(raffleMetadata, null, 2);
}

// Fetch raffle metadata from IPFS WITHOUT react-query; we do this to
// avoid ever letting potentially huge raffle lists touch localStorage,
// which can crash Firefox.
export function useMetadata(cid?: CID | string) {
  const [metadata, setMetadata] = useState<RaffleMetadata | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!cid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getFile(cid)
      .then((data) =>
        data.length
          ? RaffleMetadataSchema.parse(
              JSON.parse(new TextDecoder().decode(data))
            )
          : undefined
      )
      .then((_metadata) => {
        setIsLoading(false);
        setMetadata(_metadata);
      });
  }, [cid]);

  return {
    metadata,
    isLoading,
  };
}
